// "Tonight" on Today: whose turn it is and the overnight toilet trip plan.
// From late afternoon it leads with the night; in the day it's one quiet line.

import { AlarmClock, ArrowLeftRight, Moon, Sun, Users } from 'lucide-react';
import { useMemo } from 'react';
import { saveHousehold } from '../../data/actions.ts';
import { useEvents, useHousehold, useLiveStatus } from '../../data/hooks.ts';
import { nightTripPlan, onDuty, tonightAt, withSwap } from '../../domain/nightplan.ts';
import { inClockWindow } from '../../domain/time.ts';
import { navigate } from '../../app/router.ts';
import { Avatar } from '../../ui/bits.tsx';
import { Button, Card, CardHeader } from '../../ui/primitives.tsx';
import { toast } from '../../ui/toast.tsx';

export function TonightCard() {
  const s = useLiveStatus(60_000);
  const household = useHousehold();
  const events = useEvents();
  const weeks = s.age?.weeks ?? 12;
  const plan = useMemo(() => nightTripPlan({ household, events, weeks, now: s.now }), [household, events, weeks, s.now]);
  const hasRota = !!household.rota && Object.values(household.rota).some((d) => d?.some(Boolean));
  const hour = new Date(s.now).getHours();
  const evening = hour >= 17 || inClockWindow(s.now, household.nightStart, household.nightEnd);
  if (!plan.time && !hasRota) return null;

  // Tomorrow morning's person, looked up at 8am tomorrow (or this morning, in the small hours).
  const tomorrow8 = new Date(s.now);
  if (hour >= 12) tomorrow8.setDate(tomorrow8.getDate() + 1);
  tomorrow8.setHours(8, 0, 0, 0);
  const morning = onDuty(household, 'morning', tomorrow8.getTime());
  const day = onDuty(household, 'day', s.now);
  // One tap: hand tonight to the next person (just tonight — the rota stays as it is).
  const next = household.people.length ? household.people[(household.people.findIndex((p) => p.id === plan.who?.id) + 1) % household.people.length] : undefined;
  const swapTonight = () => {
    if (!next) return;
    saveHousehold({ rotaSwaps: withSwap(household, 'night', tonightAt(household, s.now), next.id) });
    toast({ message: `${next.name}’s doing tonight`, detail: 'Just tonight — the rota is unchanged.', tone: 'good' });
  };
  const setTime = (time: string) => {
    saveHousehold({ nightTrip: { ...household.nightTrip, time, off: false } });
    toast({ message: `Night trip moved to ${time}`, tone: 'good' });
  };

  if (!evening)
    return (
      <Card className="tonight tonight-compact">
        <div className="tonight-line">
          <Users aria-hidden />
          <span>
            {day ? (
              <>
                <strong>{day.name}</strong>’s on today
              </>
            ) : (
              'Today'
            )}
            {plan.time && plan.who && (
              <>
                {' · '}
                <strong>{plan.who.name}</strong> does the {plan.time} trip
                {morning?.id === plan.who.id && ' and the morning'}
              </>
            )}
            {plan.time && !plan.who && <> · night trip {plan.time}</>}
            {morning && morning.id !== plan.who?.id && (
              <>
                {' · '}
                <strong>{morning.name}</strong> up first
              </>
            )}
          </span>
          {plan.time && next && household.people.length > 1 && (
            <button type="button" className="link small" onClick={swapTonight}>
              Swap tonight
            </button>
          )}
          <button type="button" className="link small" onClick={() => navigate('/settings/household')}>
            Rota
          </button>
        </div>
      </Card>
    );

  return (
    <Card className="tonight">
      <CardHeader
        title="Tonight"
        icon={Moon}
        sub={plan.dryNights > 0 && !plan.proposeTime && !plan.proposeOff ? `${plan.dryNights} dry night${plan.dryNights === 1 ? '' : 's'} in a row` : undefined}
        action={
          <button type="button" className="link small" onClick={() => navigate('/settings/household')}>
            Rota & times
          </button>
        }
      />
      <ul className="tonight-list">
        <li>
          <Moon aria-hidden />
          <span>
            <strong>Bedtime wee</strong> just before {household.nightStart} — then lights out.
          </span>
        </li>
        {plan.time ? (
          <li>
            <AlarmClock aria-hidden />
            <span>
              <strong>Night trip {plan.time}</strong>
              {plan.who ? (
                <>
                  {' '}
                  — <Avatar person={plan.who} size={20} /> {plan.who.name}’s turn
                </>
              ) : (
                ' — set whose turn in the rota'
              )}
            </span>
          </li>
        ) : null}
        <li>
          <Sun aria-hidden />
          <span>
            <strong>Up at {household.nightEnd}</strong>
            {morning ? (
              <>
                {' '}
                — <Avatar person={morning} size={20} /> {morning.name}
              </>
            ) : null}
            , straight out for a wee.
          </span>
        </li>
      </ul>
      <p className="small muted">{plan.advice}</p>
      <div className="row">
        {plan.proposeTime && (
          <Button size="sm" variant="primary" onClick={() => setTime(plan.proposeTime!)}>
            Use {plan.proposeTime}
          </Button>
        )}
        {plan.proposeOff && (
          <Button size="sm" onClick={() => (saveHousehold({ nightTrip: { ...household.nightTrip, off: true } }), toast({ message: 'No more night trips — well done, Arlo!', tone: 'good' }))}>
            Stop night trips
          </Button>
        )}
        {plan.time && next && household.people.length > 1 && (
          <Button size="sm" icon={ArrowLeftRight} onClick={swapTonight}>
            {plan.who ? `${next.name} does tonight` : `${next.name} takes tonight`}
          </Button>
        )}
      </div>
    </Card>
  );
}
